import React, { useState, useEffect } from 'react';
import { Flame, CheckCircle } from 'lucide-react';

export default function Battle() {
  const [battle, setBattle] = useState(null);
  const [lastClosed, setLastClosed] = useState(null);
  const [userVoted, setUserVoted] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deviceId, setDeviceId] = useState('');

  useEffect(() => {
    // Generar o recuperar ID de dispositivo único
    let dId = localStorage.getItem('asenf_device_id');
    if (!dId) {
      dId = 'dev_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('asenf_device_id', dId);
    }
    setDeviceId(dId);

    loadBattle(dId);
    const interval = setInterval(() => loadBattle(dId), 5000);
    return () => clearInterval(interval);
  }, []);

  const loadBattle = async (currentDeviceId) => {
    try {
      const res = await fetch('/api/battles/active');
      if (res.ok) {
        const data = await res.json();
        if (!data.error) {
          setBattle(data);
          setLastClosed(null);
          // Verificar si ya votó en esta batalla específica
          const savedVote = localStorage.getItem(`voted_battle_${data.id}`);
          if (savedVote) {
            setUserVoted(parseInt(savedVote));
          } else {
            setUserVoted(null);
          }
        } else {
          setBattle(null);
          if (data.last_closed) {
            setLastClosed(data.last_closed);
          }
        }
      }
    } catch (e) { console.error('Error batalla:', e); }
    finally { setLoading(false); }
  };

  const handleVote = async (cancionId) => {
    if (userVoted || !battle) return;
    
    try {
      const res = await fetch(`/api/battles/vote/${battle.id}/${cancionId}?votante=${deviceId}`, { 
        method: 'POST' 
      });
      const data = await res.json();
      
      if (res.ok && !data.error) {
        setUserVoted(cancionId);
        localStorage.setItem(`voted_battle_${battle.id}`, cancionId.toString());
        loadBattle(deviceId);
      } else if (data.already_voted) {
        alert('Ya has votado en esta batalla');
        setUserVoted(cancionId); // Marcar como votado localmente
        localStorage.setItem(`voted_battle_${battle.id}`, cancionId.toString());
      }
    } catch (e) { console.error('Error votando:', e); }
  };

  if (loading) return <div className="text-center p-4 text-gray-400">Cargando batalla...</div>;

  if (!battle) return (
    <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-6 border border-slate-700 shadow-xl relative overflow-hidden">
      <div className="flex items-center gap-2 mb-4">
        <Flame className="w-5 h-5 text-gray-500" />
        <h3 className="text-lg font-bold text-gray-400 uppercase tracking-tighter">Batalla Musical</h3>
      </div>
      
      {lastClosed ? (
        <div className="text-center py-6 relative z-10">
          <div className="mb-4">
            <span className="bg-green-600 text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest">Batalla Finalizada</span>
          </div>
          <p className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-2">Ganador Anterior</p>
          <h4 className="text-xl font-black text-white mb-1">
            {lastClosed.votos_a >= lastClosed.votos_b ? lastClosed.cancion_a : lastClosed.cancion_b}
          </h4>
          <div className="flex items-center justify-center gap-4 mt-4 text-gray-400">
            <div>
              <p className="text-lg font-black text-white">{Math.max(lastClosed.votos_a, lastClosed.votos_b)}</p>
              <p className="text-[10px] font-bold uppercase">Votos</p>
            </div>
            <div className="w-px h-8 bg-slate-700"></div>
            <div>
              <p className="text-lg font-black text-white">{lastClosed.cerrada_en}</p>
              <p className="text-[10px] font-bold uppercase">Hora Cierre</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-10 opacity-40">
          <p className="text-5xl mb-4">⚔️</p>
          <p className="text-sm font-bold uppercase tracking-widest text-gray-500">Sin batallas activas</p>
        </div>
      )}
      
      <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-blue-500/5 blur-3xl rounded-full"></div>
    </div>
  );

  const totalVotes = (battle.votos_a || 0) + (battle.votos_b || 0);
  const aPercent = totalVotes > 0 ? ((battle.votos_a / totalVotes) * 100).toFixed(1) : 50;
  const bPercent = totalVotes > 0 ? ((battle.votos_b / totalVotes) * 100).toFixed(1) : 50;

  return (
    <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl p-4 sm:p-6 border border-slate-700 shadow-xl relative overflow-hidden group">
      {/* Glow Effect */}
      <div className="absolute -top-24 -right-24 w-48 h-48 bg-red-500/10 blur-3xl rounded-full"></div>
      
      <div className="flex items-center justify-between mb-6 relative z-10">
        <div className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-red-500 animate-pulse" />
          <h3 className="text-lg font-black text-white uppercase tracking-tighter">Batalla en Vivo</h3>
        </div>
        <div className="flex items-center gap-1">
           <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-ping"></span>
           <span className="text-[10px] font-black text-red-500 uppercase tracking-widest">Live</span>
        </div>
      </div>

      <div className="mb-6 bg-slate-900/50 p-3 rounded-xl border border-slate-700/50 text-center">
        <p className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-1">Categoría</p>
        <p className="text-sm text-white font-black">{battle.nombre}</p>
      </div>

      <div className="space-y-6 relative z-10">
        {/* Cancion A */}
        <div className="relative">
          <div className="flex justify-between items-end mb-2">
            <div className="flex-1">
              <p className="text-[10px] font-bold text-gray-500 uppercase mb-0.5">Opción A</p>
              <p className="text-sm font-black text-white leading-tight line-clamp-1">{battle.cancion_a}</p>
            </div>
            <p className="text-xl font-black text-red-500 ml-3">{battle.votos_a}</p>
          </div>
          <div className="h-2 w-full bg-slate-700 rounded-full overflow-hidden mb-3">
            <div className="h-full bg-gradient-to-r from-red-600 to-orange-500 transition-all duration-1000 ease-out" 
                 style={{ width: `${aPercent}%` }}></div>
          </div>
          <button 
            onClick={() => handleVote(battle.cancion_a_id)} 
            disabled={userVoted !== null}
            className={`w-full py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all transform active:scale-95 flex items-center justify-center gap-2 ${
              userVoted === battle.cancion_a_id 
              ? 'bg-green-600 text-white shadow-lg shadow-green-900/40' 
              : userVoted 
              ? 'bg-slate-700 text-gray-500 cursor-not-allowed opacity-50' 
              : 'bg-white text-black hover:bg-gray-200'
            }`}
          >
            {userVoted === battle.cancion_a_id ? <><CheckCircle className="w-4 h-4" /> Voto Registrado</> : 'Votar Opción A'}
          </button>
        </div>

        {/* VS Divider */}
        <div className="flex items-center gap-4 py-2">
          <div className="h-px flex-1 bg-slate-700"></div>
          <span className="text-[10px] font-black text-gray-600 italic">VS</span>
          <div className="h-px flex-1 bg-slate-700"></div>
        </div>

        {/* Cancion B */}
        <div className="relative">
          <div className="flex justify-between items-end mb-2">
            <div className="flex-1">
              <p className="text-[10px] font-bold text-gray-500 uppercase mb-0.5">Opción B</p>
              <p className="text-sm font-black text-white leading-tight line-clamp-1">{battle.cancion_b}</p>
            </div>
            <p className="text-xl font-black text-blue-500 ml-3">{battle.votos_b}</p>
          </div>
          <div className="h-2 w-full bg-slate-700 rounded-full overflow-hidden mb-3">
            <div className="h-full bg-gradient-to-r from-blue-600 to-cyan-500 transition-all duration-1000 ease-out" 
                 style={{ width: `${bPercent}%` }}></div>
          </div>
          <button 
            onClick={() => handleVote(battle.cancion_b_id)} 
            disabled={userVoted !== null}
            className={`w-full py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all transform active:scale-95 flex items-center justify-center gap-2 ${
              userVoted === battle.cancion_b_id 
              ? 'bg-green-600 text-white shadow-lg shadow-green-900/40' 
              : userVoted 
              ? 'bg-slate-700 text-gray-500 cursor-not-allowed opacity-50' 
              : 'bg-white text-black hover:bg-gray-200'
            }`}
          >
            {userVoted === battle.cancion_b_id ? <><CheckCircle className="w-4 h-4" /> Voto Registrado</> : 'Votar Opción B'}
          </button>
        </div>
      </div>

      {/* Footer Stats */}
      <div className="mt-8 pt-4 border-t border-slate-700/50 grid grid-cols-2 gap-4">
        <div className="text-center">
          <p className="text-lg font-black text-white">{totalVotes}</p>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-tighter">Votos Totales</p>
        </div>
        <div className="text-center border-l border-slate-700/50">
          <p className="text-lg font-black text-white">{battle.participantes || 0}</p>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-tighter">Participantes</p>
        </div>
      </div>
    </div>
  );
}
